import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import './BentoDashboard.css';

const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';

/* ── Custom SVG icons (stroke, currentColor, 20×20) ── */
const Icons = {
  property: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M3 17V8l7-5 7 5v9" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/><rect x="8" y="12" width="4" height="5" stroke="currentColor" strokeWidth="1.5"/></svg>
  ),
  inquiry: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M3 4h14a1 1 0 011 1v8a1 1 0 01-1 1H7l-4 3V5a1 1 0 011-1z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/><circle cx="7" cy="9" r="0.75" fill="currentColor"/><circle cx="10" cy="9" r="0.75" fill="currentColor"/><circle cx="13" cy="9" r="0.75" fill="currentColor"/></svg>
  ),
  application: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><rect x="4" y="2" width="12" height="16" rx="2" stroke="currentColor" strokeWidth="1.5"/><path d="M7 6h6M7 9h6M7 12h4" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round"/></svg>
  ),
  tenants: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="6" r="3" stroke="currentColor" strokeWidth="1.5"/><path d="M4 17c0-3.3 2.7-6 6-6s6 2.7 6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
  ),
  ledger: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><rect x="3" y="3" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="1.5"/><path d="M3 7h14" stroke="currentColor" strokeWidth="1.25"/><path d="M7 7v10" stroke="currentColor" strokeWidth="1.25"/><path d="M10 11h4M10 14h3" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round"/></svg>
  ),
  settings: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="3" stroke="currentColor" strokeWidth="1.5"/><path d="M10 2v2M10 16v2M2 10h2M16 10h2M4.22 4.22l1.42 1.42M14.36 14.36l1.42 1.42M4.22 15.78l1.42-1.42M14.36 5.64l1.42-1.42" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round"/></svg>
  ),
  calendar: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><rect x="3" y="4" width="14" height="13" rx="2" stroke="currentColor" strokeWidth="1.5"/><path d="M3 8h14" stroke="currentColor" strokeWidth="1.25"/><path d="M7 2v4M13 2v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><circle cx="10" cy="12.5" r="1" fill="currentColor"/></svg>
  ),
  wrench: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M12.5 3a4.5 4.5 0 00-4 6.6L3.7 14.4a1.5 1.5 0 002.1 2.1l4.8-4.8A4.5 4.5 0 0012.5 3z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>
  ),
  mail: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><rect x="2" y="4" width="16" height="12" rx="2" stroke="currentColor" strokeWidth="1.5"/><path d="M2 6l8 5 8-5" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>
  ),
  subscription: (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><rect x="2" y="5" width="16" height="11" rx="2" stroke="currentColor" strokeWidth="1.5"/><path d="M2 9h16" stroke="currentColor" strokeWidth="1.25"/><path d="M5 13h4" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round"/></svg>
  ),
};

function BentoDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [properties, setProperties] = useState([]);

  const [, setApplications] = useState([]);
  const [maintenance, setMaintenance] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [summary, setSummary] = useState(null);
  const [subscription, setSubscription] = useState({ subscription_status: 'free' });
  const [verification, setVerification] = useState(null);

  const [connectStatus, setConnectStatus] = useState({ connected: false });
  const [paymentsSettings, setPaymentsSettings] = useState({ payments_enabled: false });
  const [loading, setLoading] = useState(true);
  // Unread counts (same endpoints as nav badges)
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [unreadMaintenance, setUnreadMaintenance] = useState(0);
  const [unreadApplications, setUnreadApplications] = useState(0);
  // eslint-disable-next-line no-unused-vars

  const [unreadTenants, setUnreadTenants] = useState(0);
  const [unreadProperties, setUnreadProperties] = useState(0);
  const [unreadLedger, setUnreadLedger] = useState(0);
  const [onboarding, setOnboarding] = useState(null);


  const token = localStorage.getItem('token');
  const API = process.env.REACT_APP_API_BASE_URL;

  // Handle Stripe checkout return — confirm subscription and refetch
  useEffect(() => {
    if (!token) return;
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    const success = params.get('success') === 'true';
    if (success && sessionId) {
      fetch(`${API}/api/landlords/subscription/confirm`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId })
      }).then(() => {
        // Refetch subscription status
        fetch(`${API}/api/landlords/subscription`, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(r => r.json()).then(sub => { if (sub?.subscription) setSubscription(sub.subscription); else if (sub) setSubscription(sub); }).catch(() => {});
      }).catch(() => {});
      // Clean URL params
      navigate('/landlord/dashboard/v2', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, location.search, navigate]);

  useEffect(() => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    const get = (url) => fetch(`${API}${url}`, { headers }).then(r => r.ok ? r.json() : null).catch(() => null);

    Promise.all([
      get('/api/landlords/properties'),
      get('/api/landlords/applications'),
      get('/api/landlords/maintenance'),
      get('/api/landlords/active-tenants'),
      get('/api/landlords/analytics/summary'),
      get('/api/landlords/subscription'),
      get('/api/landlords/verification/status'),
      get('/api/landlords/connect/status'),
      get('/api/landlords/payments/settings'),
      get('/api/landlords/onboarding-status'),
      // Unread counts — same as nav badges
      get('/api/messages/unread-count'),
      get('/api/maintenance/unread-count'),
      get('/api/applications/unread-count'),
      get('/api/tenants/unread-count'),
      get('/api/properties/unread-count'),
      get('/api/ledger/unread-count'),
    ]).then(([props, apps, maint, tens, sum, sub, ver, conn, pay, onboardData,
             msgUn, maintUn, appUn, tenUn, propUn, ledgUn]) => {
      if (props?.properties) setProperties(props.properties);
      if (apps?.applications) setApplications(apps.applications);
      if (Array.isArray(maint)) setMaintenance(maint);
      else if (maint?.requests) setMaintenance(maint.requests);
      if (tens?.tenants) setTenants(tens.tenants);
      else if (Array.isArray(tens)) setTenants(tens);
      if (sum) setSummary(sum);
      if (sub?.subscription) setSubscription(sub.subscription);
      else if (sub) setSubscription(sub);
      if (ver?.verification) setVerification(ver.verification);
      else if (ver) setVerification(ver);
      if (conn) setConnectStatus(conn);
      if (pay) setPaymentsSettings(pay);
      if (onboardData) setOnboarding(onboardData);
      // Unread counts
      if (msgUn?.count !== undefined) setUnreadMessages(msgUn.count);
      if (maintUn?.count !== undefined) setUnreadMaintenance(maintUn.count);
      if (appUn?.count !== undefined) setUnreadApplications(appUn.count);
      if (tenUn?.count !== undefined) setUnreadTenants(tenUn.count);
      if (propUn?.count !== undefined) setUnreadProperties(propUn.count);
      if (ledgUn?.count !== undefined) setUnreadLedger(ledgUn.count);
      setLoading(false);
    });
  }, [token, API]);

  if (loading) {
    return <main className="App-main"><p style={{ textAlign: 'center', padding: '3rem', color: 'var(--light-text-color)' }}>Loading dashboard...</p></main>;
  }

  const subscriptionActive = subscription?.subscription_status === 'active';
  const isVerified = verification?.is_verified || verification?.id_status === 'verified';
  const stripeConnected = connectStatus?.connected && connectStatus?.payouts_enabled;
  const paymentsEnabled = paymentsSettings?.payments_enabled;
  const activeProperties = properties.filter(p => p.status !== 'archived');
  // Counts — use unread endpoints (same source as nav badges)
  const openMaintenanceCount = maintenance.filter(req => req.status !== 'resolved').length;

  // Summary stats
  const occupancyRate = summary?.occupancyRate || 0;
  const activeLeases = summary?.activeLeases || 0;
  const totalApplications = summary?.funnel?.reduce((sum, row) => sum + (row.count || 0), 0) || 0;
  const approvedCount = summary?.funnel?.find(row => row.status === 'approved')?.count || 0;
  const approvalRate = totalApplications === 0 ? 0 : Math.round((approvedCount / totalApplications) * 100);
  const rentTotals = (summary?.rent || []).reduce((acc, row) => { acc[row.status] = Number(row.total) || 0; return acc; }, {});
  const rentDueToday = (summary?.rent || []).reduce((acc, row) => { acc[row.status] = Number(row.total_due_today) || 0; return acc; }, {});
  const rentCollected = rentTotals.paid || 0;
  const rentOutstanding = Object.entries(rentDueToday).reduce((sum, [status, total]) => status === 'paid' ? sum : sum + total, 0);



  // eslint-disable-next-line no-unused-vars
  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  const fmtCurrency = (val) => {
    const num = Number(val);
    if (isNaN(num)) return '$0';
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(num / 100);
  };

  const fmtRent = (val) => {
    const num = Number(val);
    if (isNaN(num)) return '$0';
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', minimumFractionDigits: 0 }).format(num);
  };

  // Activity items for "new items" card
  const activityItems = [
    { label: 'Upcoming viewings', count: 0, link: '/landlord/viewings', icon: Icons.calendar },
    { label: 'New applications', count: unreadApplications, link: '/landlord/applicants', icon: Icons.application },
    { label: 'Open maintenance', count: unreadMaintenance || openMaintenanceCount, link: '/landlord/maintenance', icon: Icons.wrench },
    { label: 'Unread messages', count: unreadMessages, link: '/landlord/messages', icon: Icons.mail },
    { label: 'Properties', count: unreadProperties, link: '/landlord/dashboard', icon: Icons.property },
    { label: 'Tenants', count: unreadTenants, link: '/landlord/my-tenants', icon: Icons.tenants },
    { label: 'Payments', count: unreadLedger, link: '/landlord/ledger', icon: Icons.ledger },
  ].filter(item => item.count > 0);

  return (
    <main className="App-main">
      {location.state?.upgradeRequired && (
        <div className="dashboard-section notice-card" style={{ marginBottom: '1rem', borderLeft: '4px solid var(--accent-color, #28b6c8)', padding: '0.75rem 1rem' }}>
          <p style={{ margin: 0 }}>You're on the free plan, which allows 1 property. <Link to="/pricing" style={{ fontWeight: 600, color: 'var(--accent-color, #28b6c8)' }}>Upgrade to Pro</Link> to add more listings.</p>
        </div>
      )}
      <div className="bento-header">
        <div>
          <h2>Dashboard</h2>
          <p>Your portfolio at a glance.</p>
        </div>
      </div>

      <div className="bento-dashboard">



        {/* ── 0. Onboarding Checklist ──────────────────────── */}
        {(() => {
          const idDone = verification?.id_status === 'verified' || verification?.id_status === 'id_verified';
          const idPending = verification?.id_status === 'pending';
          const subDone = subscription?.subscription_status === 'active';
          const hasProp = properties.length > 0;
          const hasShared = properties.some(p => p.share_token);
          const bankDone = connectStatus?.connected && connectStatus?.payouts_enabled;
          const requiredDone = hasProp && hasShared && idDone && (onboarding?.profile_complete);
          const allDone = requiredDone && subDone && bankDone;
          if (allDone) return null;

          const handleStartIdentity = async () => {
            try {
              const token = localStorage.getItem('token');
              const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/verification/identity/start`, {
                method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
              });
              const data = await res.json();
              if (data.client_secret) {
                const stripe = await loadStripe(STRIPE_PUBLISHABLE_KEY);
                const { error: stripeError } = await stripe.verifyIdentity(data.client_secret);
                if (stripeError) {
                  // User closed or cancelled — reset DB and local state
                  setVerification(prev => prev ? { ...prev, id_status: 'unverified' } : prev);
                  fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/verification/identity/cancel`, {
                    method: 'POST', headers: { Authorization: `Bearer ${token}` }
                  }).catch(() => {});
                  return;
                }
                // Completed — refetch verification status
                try {
                  const vRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/verification/status`, {
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  const vData = await vRes.json();
                  if (vData.verification) setVerification(vData.verification);
                } catch (e) { /* silent */ }
              }
            } catch (err) { console.error('Identity verification error:', err); }
          };



          const handleUpgrade = async (plan) => {
            try {
              const token = localStorage.getItem('token');
              const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/subscription/upgrade`, {
                method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ plan })
              });
              const data = await res.json();
              if (data.url) window.location.href = data.url;
              else if (data.error) alert(data.error);
            } catch (err) { console.error('Upgrade error:', err); }
          };

          const handleConnect = async (e) => {
            e.stopPropagation();
            try {
              const token = localStorage.getItem('token');
              const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/landlords/connect/onboard`, {
                method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
              });
              const data = await res.json();
              if (data.url) window.location.href = data.url;
              else if (res.status === 402) alert('Please subscribe to a Pro plan before connecting your bank account.');
              else if (data.error) alert(data.error);
            } catch (err) { console.error('Connect error:', err); }
          };

          const hasMismatch = verification?.id_status === 'mismatch';
          const profileDone = onboarding?.profile_complete;
          const steps = [
            {
              key: 'property', label: 'Add your first property',
              description: 'Create a listing to start screening tenants.',
              done: hasProp,
              action: hasProp ? null : (
                <button className="secondary-button" onClick={() => navigate('/landlord/properties/new')} style={{ fontSize: '0.85rem', padding: '0.4rem 0.75rem' }}>
                  Add property
                </button>
              )
            },
            {
              key: 'profile', label: 'Complete your profile',
              description: 'Required to publish listings and run screening.',
              done: !!profileDone,
              action: profileDone ? null : (
                <button className="secondary-button" onClick={() => navigate('/landlord/analytics#profile')} style={{ fontSize: '0.85rem', padding: '0.4rem 0.75rem' }}>
                  Complete profile
                </button>
              )
            },
            {
              key: 'verify', label: 'Verify your identity',
              description: idPending ? 'Verification in progress — usually takes under a minute.' : hasMismatch ? 'Profile details didn\'t match your ID. Update and retry.' : 'Quick ID check to build tenant trust.',
              done: idDone, pending: idPending,
              action: idPending ? null : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <button className="secondary-button" onClick={handleStartIdentity} disabled={!STRIPE_PUBLISHABLE_KEY} style={{ fontSize: '0.85rem', padding: '0.4rem 0.75rem' }}>
                    {hasMismatch ? 'Retry' : 'Verify now'}
                  </button>
                  <span className="camera-trouble-wrap">
                    <button
                      type="button"
                      className="status-pill warning"
                      style={{ fontSize: '0.75rem', cursor: 'pointer', border: 'none' }}
                      onClick={(e) => { e.stopPropagation(); const el = e.currentTarget.nextElementSibling; el.classList.toggle('camera-hint-visible'); }}
                    >
                      Camera trouble?
                    </button>
                    <div className="camera-hint-box">
                      <p style={{ margin: '0 0 0.5rem', fontWeight: 600 }}>Camera not working?</p>
                      <p style={{ margin: '0 0 0.75rem' }}>
                        Some browsers block camera access for identity verification. Try opening this page in Firefox instead.
                      </p>
                      <a
                        href="https://www.google.com/search?q=download+Firefox+browser+for+my+device"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="primary-button"
                        style={{ fontSize: '0.8rem', padding: '0.35rem 0.75rem', textDecoration: 'none', display: 'inline-block' }}
                      >
                        Get Firefox
                      </a>
                    </div>
                  </span>
                </div>
              )
            },
            {
              key: 'share', label: 'Create an invitation link',
              description: 'Generate a link to invite applicants to your listing.',
              done: hasShared,
              action: hasShared ? null : (
                <button className="secondary-button" onClick={() => navigate('/landlord/dashboard')} disabled={!hasProp} style={{ fontSize: '0.85rem', padding: '0.4rem 0.75rem', opacity: hasProp ? 1 : 0.5 }}>
                  {hasProp ? 'My properties' : 'Add a property first'}
                </button>
              )
            },
            {
              key: 'subscribe', label: 'Upgrade to Pro',
              description: 'Unlock rent collection, screening, and more.',
              done: subDone || localStorage.getItem('rentual_skip_step_subscribe') === 'true', optional: true,
              action: subDone ? null : (
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <button className="secondary-button" onClick={() => handleUpgrade('monthly')} style={{ fontSize: '0.85rem', padding: '0.4rem 0.75rem' }}>Monthly</button>
                  <button className="secondary-button" onClick={() => handleUpgrade('annual')} style={{ fontSize: '0.85rem', padding: '0.4rem 0.75rem' }}>Annual (save 20%)</button>
                </div>
              )
            },
            {
              key: 'connect', label: 'Connect your bank for payments',
              description: 'Accept rent payments directly to your account.',
              done: bankDone || localStorage.getItem('rentual_skip_step_connect') === 'true', optional: true,
              action: bankDone ? null : (
                <button className="secondary-button" onClick={handleConnect} disabled={!subDone} style={{ fontSize: '0.85rem', padding: '0.4rem 0.75rem', opacity: subDone ? 1 : 0.5 }}>
                  {subDone ? 'Connect Stripe' : 'Requires Pro subscription'}
                </button>
              )
            }
          ];

          const currentStep = steps.find(s => !s.done);
          const currentIndex = currentStep ? steps.indexOf(currentStep) : steps.length;

          if (!currentStep) return null;

          return (
            <div className="bento-card bento-card-full" style={{ borderLeft: '4px solid var(--accent-color, #28b6c8)' }}>
              {/* Progress dots */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginBottom: '0.75rem' }}>
                {steps.map((s, i) => (
                  <div key={s.key} style={{
                    width: i === currentIndex ? '20px' : '8px',
                    height: '8px',
                    borderRadius: '4px',
                    background: s.done ? 'var(--accent-color, #28b6c8)' : i === currentIndex ? 'var(--accent-color, #28b6c8)' : 'var(--border-color, #e5e7eb)',
                    opacity: s.done ? 0.5 : 1,
                    transition: 'all 0.3s'
                  }} />
                ))}
                <span style={{ fontSize: '0.75rem', color: 'var(--light-text-color)', marginLeft: '0.5rem' }}>
                  Step {currentIndex + 1} of {steps.length}
                </span>
              </div>

              {/* Current step */}
              <strong style={{ fontSize: '1rem', display: 'block', marginBottom: '0.25rem' }}>
                {currentStep.label}
                {currentStep.optional && <span style={{ fontSize: '0.7rem', fontWeight: 400, color: 'var(--light-text-color)', marginLeft: '0.4rem' }}>optional</span>}
              </strong>
              <p style={{ margin: '0 0 0.75rem', fontSize: '0.85rem', color: 'var(--light-text-color)' }}>
                {currentStep.description}
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                {currentStep.action}
                {currentStep.optional && (
                  <button className="ghost-button" style={{ fontSize: '0.8rem', padding: '0.35rem 0.5rem', color: 'var(--light-text-color)' }}
                    onClick={() => {
                      const skipKey = `rentual_skip_step_${currentStep.key}`;
                      localStorage.setItem(skipKey, 'true');
                      window.location.reload();
                    }}>
                    Skip
                  </button>
                )}
              </div>
            </div>
          );
        })()}

        {/* ── 1. Quick Actions (navigation hub) ──────────────── */}
        <div className="bento-card bento-card-tall">
          <span className="bento-card-label">Quick actions</span>
          <div className="bento-actions">
            <Link to="/landlord/screenings" className="bento-action-link" style={{ position: 'relative', fontWeight: 600, color: 'var(--accent-color, #28b6c8)' }}>
              <span className="bento-action-icon bento-action-icon-svg"><svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M8 14a6 6 0 110-12 6 6 0 010 12z" stroke="currentColor" strokeWidth="1.5"/><path d="M12.5 12.5L17 17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><path d="M6 8h4M8 6v4" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round"/></svg></span> Screen a tenant
            </Link>
            <Link to="/landlord/dashboard" className="bento-action-link" style={{ position: 'relative' }}>
              <span className="bento-action-icon bento-action-icon-svg">{Icons.property}</span> My properties
              {unreadProperties > 0 && <span className="nav-badge" style={{ marginLeft: '0.35rem' }}>{unreadProperties}</span>}
            </Link>
            <Link to="/landlord/viewings" className="bento-action-link">
              <span className="bento-action-icon bento-action-icon-svg">{Icons.calendar}</span> Viewings
            </Link>
            <Link to="/landlord/applicants" className="bento-action-link" style={{ position: 'relative' }}>
              <span className="bento-action-icon bento-action-icon-svg">{Icons.application}</span> Applications
              {unreadApplications > 0 && <span className="nav-badge" style={{ marginLeft: '0.35rem' }}>{unreadApplications}</span>}
            </Link>
            <Link to="/landlord/my-tenants" className="bento-action-link" style={{ position: 'relative' }}>
              <span className="bento-action-icon bento-action-icon-svg">{Icons.tenants}</span> My tenants
              {unreadTenants > 0 && <span className="nav-badge" style={{ marginLeft: '0.35rem' }}>{unreadTenants}</span>}
            </Link>
            <Link to="/landlord/ledger" className="bento-action-link" style={{ position: 'relative' }}>
              <span className="bento-action-icon bento-action-icon-svg">{Icons.ledger}</span> Payments
              {unreadLedger > 0 && <span className="nav-badge" style={{ marginLeft: '0.35rem' }}>{unreadLedger}</span>}
            </Link>
            <Link to="/landlord/analytics#profile" className="bento-action-link">
              <span className="bento-action-icon bento-action-icon-svg">{Icons.tenants}</span> Profile
            </Link>
            <Link to="/landlord/analytics#subscription" className="bento-action-link">
              <span className="bento-action-icon bento-action-icon-svg">{Icons.subscription}</span> Subscription
            </Link>
            <Link to="/landlord/settings" className="bento-action-link">
              <span className="bento-action-icon bento-action-icon-svg">{Icons.settings}</span> Settings
            </Link>
          </div>
        </div>

        {/* ── 2. KPIs ──────────────────────────────────────── */}
        <div className="bento-card">
          <span className="bento-card-label">Overview</span>
          <div className="bento-kpi-row">
            <div className="bento-kpi">
              <div className="bento-kpi-value">{activeProperties.length}</div>
              <div className="bento-kpi-label">{activeProperties.length === 1 ? 'Property' : 'Properties'}</div>
            </div>
            <div className="bento-kpi">
              <div className="bento-kpi-value">{activeLeases}</div>
              <div className="bento-kpi-label">Active leases</div>
            </div>
            <div className="bento-kpi">
              <div className="bento-kpi-value">{occupancyRate}%</div>
              <div className="bento-kpi-label">Occupancy</div>
            </div>
            <div className="bento-kpi">
              <div className="bento-kpi-value">{tenants.length}</div>
              <div className="bento-kpi-label">Tenants</div>
            </div>
            <div className="bento-kpi">
              <div className="bento-kpi-value" style={{ color: rentCollected > 0 ? '#22c55e' : undefined }}>{fmtCurrency(rentCollected)}</div>
              <div className="bento-kpi-label">Collected</div>
            </div>
            {rentOutstanding > 0 && (
              <div className="bento-kpi bento-kpi--clickable" onClick={() => navigate('/landlord/ledger?filter=outstanding')} title="View outstanding payments">
                <div className="bento-kpi-value" style={{ color: '#f5a623' }}>{fmtCurrency(rentOutstanding)}</div>
                <div className="bento-kpi-label">Outstanding →</div>
              </div>
            )}
          </div>
        </div>

        {/* ── 2. Analytics Quick View (trends at a glance) ─────── */}
        <div className="bento-card">
          <span className="bento-card-label">Analytics</span>
          <div className="bento-kpi-row" style={{ marginBottom: '0.75rem' }}>
            <div className="bento-kpi">
              <div className="bento-kpi-value">{summary?.views7 || 0}</div>
              <div className="bento-kpi-label">Views (7d)</div>
            </div>
            <div className="bento-kpi">
              <div className="bento-kpi-value">{summary?.views30 || 0}</div>
              <div className="bento-kpi-label">Views (30d)</div>
            </div>
            <div className="bento-kpi">
              <div className="bento-kpi-value">{totalApplications}</div>
              <div className="bento-kpi-label">Applications</div>
            </div>
            <div className="bento-kpi">
              <div className="bento-kpi-value">{approvalRate}%</div>
              <div className="bento-kpi-label">Approval rate</div>
            </div>
          </div>
          <div style={{ marginTop: 'auto', paddingTop: '0.75rem' }}>
            <Link to="/landlord/analytics#portfolio" className="secondary-button" style={{ display: 'inline-flex', fontSize: '0.85rem', padding: '0.5rem 1rem' }}>
              View full analytics
            </Link>
          </div>
        </div>

        {/* ── 3. Needs Attention ─────────────────────────────── */}
        <div className="bento-card bento-card-wide">
          <span className="bento-card-label">Needs attention</span>
          {activityItems.length === 0 ? (
            <p className="bento-card-subtitle">All clear. Nothing needs your attention.</p>
          ) : (
            <div className="bento-actions">
              {activityItems.map(item => (
                <Link to={item.link} className="bento-action-link" key={item.label}>
                  <span className="bento-action-icon bento-action-icon-svg">{item.icon}</span>
                  <span style={{ flex: 1 }}>{item.label}</span>
                  <span className="bento-activity-count">{item.count}</span>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* ── 4. Rent Health (am I getting paid?) ──────────────── */}
        {(summary?.rent || []).length > 0 && (
          <div className="bento-card">
            <span className="bento-card-label">Rent health</span>
            <div className="bento-kpi-row" style={{ marginBottom: '0.75rem' }}>
              <div className="bento-kpi">
                <div className="bento-kpi-value" style={{ color: '#22c55e' }}>{fmtCurrency(rentCollected)}</div>
                <div className="bento-kpi-label">Collected</div>
              </div>
              {rentOutstanding > 0 && (
                <div className="bento-kpi bento-kpi--clickable" onClick={() => navigate('/landlord/ledger?filter=outstanding')} title="View outstanding payments">
                  <div className="bento-kpi-value" style={{ color: '#f5a623' }}>{fmtCurrency(rentOutstanding)}</div>
                  <div className="bento-kpi-label">Outstanding →</div>
                </div>
              )}
              {(summary?.collectedThisMonth !== undefined) && (
                <div className="bento-kpi">
                  <div className="bento-kpi-value">{fmtCurrency(summary.collectedThisMonth)}</div>
                  <div className="bento-kpi-label">This month</div>
                </div>
              )}
            </div>
            <div className="bento-funnel">
              {summary.rent.map((row, i) => (
                <div className="bento-funnel-row" key={i}>
                  <span className="bento-funnel-label">{(row.status || '').replace(/_/g, ' ')}</span>
                  <div className="bento-funnel-bar-bg">
                    <div className="bento-funnel-bar" style={{
                      width: `${(rentCollected + rentOutstanding) > 0 ? (Number(row.total) / (rentCollected + rentOutstanding)) * 100 : 0}%`,
                      background: row.status === 'paid' ? '#22c55e' : '#f5a623'
                    }} />
                  </div>
                  <span className="bento-funnel-count">{fmtCurrency(Number(row.total) || 0)}</span>
                </div>
              ))}
            </div>
            <Link to="/landlord/ledger" className="secondary-button" style={{ marginTop: '0.75rem', display: 'inline-flex', fontSize: '0.85rem', padding: '0.5rem 1rem' }}>
              View ledger
            </Link>
          </div>
        )}

        {/* ── 5. Properties ──────────────────────────────────── */}
        <div className="bento-card bento-card-wide">
          <span className="bento-card-label">Properties</span>
          {activeProperties.length === 0 ? (
            <div>
              <p className="bento-card-subtitle">No properties yet. Add your first listing to get started.</p>
              <Link to="/landlord/properties/new" className="secondary-button" style={{ marginTop: '0.75rem', display: 'inline-flex' }}>Add property</Link>
            </div>
          ) : (
            <div className="bento-property-list">
              {activeProperties.slice(0, 5).map(p => (
                <Link to={`/landlord/properties/edit/${p.id}`} className="bento-property-item" key={p.id}>
                  {p.images?.[0] ? (
                    <img src={p.images[0]} alt="" className="bento-property-thumb" />
                  ) : (
                    <div className="bento-property-thumb" />
                  )}
                  <div className="bento-property-info">
                    <div className="bento-property-name">{p.address || 'Untitled'}</div>
                    <div className="bento-property-meta">
                      {p.bedrooms && `${p.bedrooms} bed`}{p.bathrooms && ` · ${p.bathrooms} bath`}
                      {p.rent && ` · ${fmtRent(p.rent)}/mo`}
                    </div>
                  </div>
                  <span className={`bento-status-pill bento-status-${(p.status || 'available').toLowerCase().replace(/\s/g, '_')}`}>
                    {p.status || 'available'}
                  </span>
                </Link>
              ))}
              {activeProperties.length > 5 && (
                <Link to="/landlord/dashboard" className="bento-card-subtitle" style={{ marginTop: '0.5rem', display: 'block' }}>
                  + {activeProperties.length - 5} more
                </Link>
              )}
            </div>
          )}
        </div>

        {/* ── 6. Application Pipeline ──────────────────────── */}
        {totalApplications > 0 && (
          <div className="bento-card">
            <span className="bento-card-label">Application pipeline</span>
            <div className="bento-kpi-row" style={{ marginBottom: '0.75rem' }}>
              <div className="bento-kpi">
                <div className="bento-kpi-value">{totalApplications}</div>
                <div className="bento-kpi-label">Total</div>
              </div>
              <div className="bento-kpi">
                <div className="bento-kpi-value" style={{ color: '#22c55e' }}>{approvedCount}</div>
                <div className="bento-kpi-label">Approved</div>
              </div>
            </div>
            {(summary?.funnel || []).length > 0 && (
              <div className="bento-funnel">
                {summary.funnel.map((row, i) => (
                  <div className="bento-funnel-row" key={i}>
                    <span className="bento-funnel-label">{(row.status || '').replace(/_/g, ' ')}</span>
                    <div className="bento-funnel-bar-bg">
                      <div className="bento-funnel-bar" style={{ width: `${totalApplications ? (row.count / totalApplications) * 100 : 0}%` }} />
                    </div>
                    <span className="bento-funnel-count">{row.count}</span>
                  </div>
                ))}
              </div>
            )}
            <Link to="/landlord/applicants" className="secondary-button" style={{ marginTop: '0.75rem', display: 'inline-flex', fontSize: '0.85rem', padding: '0.5rem 1rem' }}>
              View applications
            </Link>
          </div>
        )}

        {/* ── 7. Upcoming Viewings ───────────────────────── */}
        <div className="bento-card">
          <span className="bento-card-label">Upcoming viewings</span>
          <p className="bento-card-subtitle">Manage viewings and time slots from the Viewings page.</p>
          <Link to="/landlord/viewings" className="secondary-button" style={{ marginTop: '0.75rem', display: 'inline-flex', fontSize: '0.85rem', padding: '0.5rem 1rem' }}>
            Manage viewings
          </Link>
        </div>

        {/* ── 9. Maintenance ──────────────────────────────── */}
        <div className="bento-card">
          <span className="bento-card-label">Maintenance</span>
          {maintenance.length === 0 ? (
            <p className="bento-card-subtitle">No maintenance requests.</p>
          ) : (
            <>
              <div className="bento-kpi-row" style={{ marginBottom: '0.75rem' }}>
                <div className="bento-kpi">
                  <div className="bento-kpi-value">{openMaintenanceCount}</div>
                  <div className="bento-kpi-label">Open</div>
                </div>
                <div className="bento-kpi">
                  <div className="bento-kpi-value">{maintenance.filter(r => r.status === 'resolved').length}</div>
                  <div className="bento-kpi-label">Resolved</div>
                </div>
              </div>
              {(summary?.maintenance || []).length > 0 && (
                <div className="bento-funnel">
                  {summary.maintenance.map((row, i) => (
                    <div className="bento-funnel-row" key={i}>
                      <span className="bento-funnel-label">{(row.status || '').replace(/_/g, ' ')}</span>
                      <div className="bento-funnel-bar-bg">
                        <div className="bento-funnel-bar" style={{
                          width: `${maintenance.length > 0 ? (row.count / maintenance.length) * 100 : 0}%`,
                          background: row.status === 'resolved' ? '#22c55e' : 'var(--accent-color)'
                        }} />
                      </div>
                      <span className="bento-funnel-count">{row.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
          <Link to="/landlord/maintenance" className="secondary-button" style={{ marginTop: '0.75rem', display: 'inline-flex', fontSize: '0.85rem', padding: '0.5rem 1rem' }}>
            View requests
          </Link>
        </div>



        {/* ── 11. Action Needed / Account (bottom) ────────── */}
        {subscriptionActive && isVerified && stripeConnected && paymentsEnabled && (
          <div className="bento-card">
            <span className="bento-card-label">Account</span>
            <div className="bento-actions">
              <div className="bento-action-link" style={{ cursor: 'default' }}>
                <span style={{ color: '#22c55e', fontSize: '0.9rem' }}>✓</span>
                <span style={{ flex: 1 }}>Pro plan</span>
                <span className="bento-card-subtitle" style={{ margin: 0 }}>
                  {subscription?.subscription_ends_at
                    ? `Renews ${new Date(subscription.subscription_ends_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`
                    : 'Active'}
                </span>
              </div>
              <div className="bento-action-link" style={{ cursor: 'default' }}>
                <span style={{ color: '#22c55e', fontSize: '0.9rem' }}>✓</span>
                <span>Identity verified</span>
              </div>
              <div className="bento-action-link" style={{ cursor: 'default' }}>
                <span style={{ color: '#22c55e', fontSize: '0.9rem' }}>✓</span>
                <span>Payments active</span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
              <Link to="/landlord/analytics#subscription" className="secondary-button" style={{ fontSize: '0.8rem', padding: '0.4rem 0.75rem' }}>Billing</Link>
              <Link to="/landlord/settings" className="secondary-button" style={{ fontSize: '0.8rem', padding: '0.4rem 0.75rem' }}>Settings</Link>
            </div>
          </div>
        )}

      </div>
    </main>
  );
}

export default BentoDashboard;
